import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import React, { useCallback, useEffect, useState } from "react";
import type { ComponentType } from "react";
import FeelingSelect from "./components/FeelingSelect";
import ButtonsSection from "components/ui/buttons/ButtonsSection";
import ChooseAnotherDate from "./components/ChooseAnotherDate";
import { useHomeData } from "./hooks/useHomeData";
import { Drawer } from "vaul";
import "../../../src/app/Vaul.css";
import type { ExistingRecord } from "../check-in/checkin-context";
import { CheckInProvider } from "../check-in/checkin-context";
import { CheckInAdd } from "../check-in/CheckInAdd/CheckInAdd";
import CheckInView from "../check-in/CheckInView/CheckInView";
import { useAppState } from "../../contexts/UserContext";
import { useAuth } from "../../contexts/AuthContext";
import WeeklyOverview from "../weeklyOverview/components/WeeklyOverview";
import { useHasMoodsInLastEndedWeek } from "../weeklyOverview/hooks/useHasMoodsInLastEndedWeek";
import { useLocation, useNavigate } from "react-router-dom";
import { getHasSeenTeamDemo } from "../teams/teamDemoStorage";
import { consumeHideHomeChromeFromIntro4 } from "../intro/introFlowStorage";
import { useTeam } from "../../contexts/TeamContext";
import "../login/components/AuthOptionsButtons.css";
import SheetSide from "./components/SheetSide";
import { useSubscriptionStatus } from "@/features/home/hooks/useSubscriptionStatus";
import { useStripeSubscriptionStatus } from "@/features/settings/hooks/useStripeSubscriptionStatus";
import { useAutoJoinOrganizationByEmailDomain } from "@/features/home/hooks/useAutoJoinOrganizationByEmailDomain";
import * as Sentry from "@sentry/react";
import { useFreeAccountCheck } from "@/features/home/hooks/useFreeAccountCheck";
import axios from "axios";
import { getOrganization } from "@/api/organizationApi";

const INNER_WRAPPER_STYLE = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  width: "100%",
} as const;

type CheckInState = {
  feelingName?: string | null;
  selectedDate?: string | Date | null;
  existingRecord?: ExistingRecord | null;
  feelingNames?: string[];
  imageSrc?: string;
};

type CheckInAddProps = {
  stateOverride?: CheckInState | null;
  onClose?: () => void;
  onSaveSuccess?: (result: unknown) => void;
};

type CheckInViewProps = {
  stateOverride?: CheckInState | null;
  onClose?: () => void;
};

const CheckInAddWithState = CheckInAdd as ComponentType<CheckInAddProps>;
const CheckInViewWithState = CheckInView as ComponentType<CheckInViewProps>;

const WEEKDAY_INDEX: Record<string, number> = {
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
};

function isTodayCheckInDay(checkInDays: unknown): boolean {
  if (!Array.isArray(checkInDays)) return false;
  const today = new Date().getDay();
  return checkInDays.some((day) => WEEKDAY_INDEX[day] === today);
}

export default function Home() {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [feelings, setFeelings] = useState<any | null>(null);
  const [checkInState, setCheckInState] = useState<CheckInState | null>(null);
  const [showWeeklyReview, setShowWeeklyReview] = useState<boolean | null>(
    true
  );
  const [hasShownWeeklyReview, setHasShownWeeklyReview] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  const navigate = useNavigate();

  const {
    moodImages,
    feelingNames,
    imagesReady,
    selectedDate,
    setSelectedDate,
    isVisible,
  } = useHomeData();

  const { state } = useLocation();
  const {
    isSubscribedStripe,
    themeId,
    collectionId,
    isLoading,
    loadingStates,
  } = useAppState();
  const { userId, accountOrganizationId } = useAuth();
  const {
    hasActiveSubscription,
    isStillChecking,
    leadUid,
  } = useSubscriptionStatus({ userId });

  const { isFreeAccount } = useFreeAccountCheck({ userId });

  const { hasStripeSubscription: hasUserStripeSubscription } =
    useStripeSubscriptionStatus(userId);
  const { hasStripeSubscription: hasLeadStripeSubscription } =
    useStripeSubscriptionStatus(leadUid);
  const hasStripeSubscriptionFromHooks =
    hasUserStripeSubscription || hasLeadStripeSubscription;
  const hasStripeSubscription =
    typeof state?.hasStripeSubscription === "boolean"
      ? state.hasStripeSubscription
      : hasStripeSubscriptionFromHooks || isSubscribedStripe;
  const { teams, teamId, organizationId } = useTeam();
  const activeTeam = teams.find((team: any) => team.id == teamId);
  const isCheckInDay = isTodayCheckInDay(activeTeam?.notifications);
  useAutoJoinOrganizationByEmailDomain({ organizationId, teamId });
  const [hideHomeChromeFromIntro4] = useState(() =>
    consumeHideHomeChromeFromIntro4()
  );
  const hasMoodsInLastEndedWeek = useHasMoodsInLastEndedWeek();
  const isOrganizationIdLoading =
    isLoading || loadingStates?.userData === true;
  const isFriday = new Date().getDay() == 5;

  const centeredContainerStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "calc(100vh - 80px)"
  } as const;


  useEffect(() => {
    axios
      .get(process.env.BASE_URL + "/feeling", {
        params: {
          userId: userId,
        },
        headers: {
          "Content-Type": "application/json",
        },
      })
      .then((response) => {
        setFeelings(response.data);
      })
      .catch((error) => {
        console.error("Error fetching feelings:", error);
      });
  }, [])

  useEffect(() => {
    if (!accountOrganizationId) return;
    getOrganization(accountOrganizationId).catch((error) => {
      console.error("Error fetching organization:", error);
    });
  }, [accountOrganizationId]);

  useEffect(() => {
    if (!userId || isOrganizationIdLoading) return;
  }, [userId, isOrganizationIdLoading, organizationId, navigate]);

  useEffect(() => {
    if (!hasMoodsInLastEndedWeek) return;
    const timeout = setTimeout(() => setIsDrawerOpen(true), 500);
    return () => clearTimeout(timeout);
  }, [hasMoodsInLastEndedWeek]);

  const [showAddTeamModal, setShowAddTeamModal] = useState(false);
  const handleAddTeamDrawerChange = useCallback((nextOpen: boolean) => {
    setShowAddTeamModal(nextOpen);
  }, []);

  const handleMoodSelected = useCallback(
    (payload: CheckInState) => {
      setCheckInState({
        ...payload,
        feelingNames,
      });
      setIsDrawerOpen(true);
    },
    [feelingNames, hasStripeSubscription]
  );

  const resetCheckIn = useCallback(() => {
    setCheckInState(null);
    setResetKey((key) => key + 1);
  }, []);

  const handleDrawerChange = useCallback(
    (nextOpen: boolean) => {
      setIsDrawerOpen(nextOpen);

      if (nextOpen) {
        setShowWeeklyReview(hasMoodsInLastEndedWeek && !hasShownWeeklyReview);
      } else {
        setHasShownWeeklyReview(true);
        setShowWeeklyReview(false);
        resetCheckIn();
      }
    },
    [resetCheckIn, hasShownWeeklyReview, hasMoodsInLastEndedWeek]
  );

  const closeDrawer = useCallback(() => {
    setHasShownWeeklyReview(true);
    setShowWeeklyReview(false);
    handleDrawerChange(false);
  }, [handleDrawerChange]);


  Sentry.setUser({ id: userId, userId: userId });

  return (
    <>
      <div className="page-container">
        <div className="content-container">
          <div className={`transition-wrapper ${isVisible ? "fade-in" : ""}`}>
            <div className="home-container" style={centeredContainerStyle}>
              {teamId != null && (
                <>
                  <div
                    className="home-inner-wrapper"
                    style={INNER_WRAPPER_STYLE}
                  >
                    {!isCheckInDay &&
                    localStorage.getItem("checkIn") !== "done1" && (
                      <Drawer.Root
                        open={isDrawerOpen}
                        onOpenChange={handleDrawerChange}
                      >
                        <Drawer.Trigger className="vaul-trigger">
                          <FeelingSelect
                            moodImages={moodImages}
                            relevantMoods={[]}
                            selectedDate={selectedDate}
                            feelingNames={feelingNames}
                            imagesReady={imagesReady}
                            onMoodSelected={handleMoodSelected}
                            resetKey={resetKey}
                            theme={themeId}
                            collection={collectionId}
                          />
                        </Drawer.Trigger>
                        <Drawer.Portal>
                          <Drawer.Overlay className="vaul-overlay" />
                          <Drawer.Content className="vaul-content">
                            <div className="vaul-panel">
                              <div aria-hidden className="vaul-handle" />
                              {showWeeklyReview && (
                                <WeeklyOverview
                                  setShowWeeklyReview={setShowWeeklyReview}
                                />
                              )}
                              {checkInState ? (
                                <CheckInProvider
                                  initialfeelingName={checkInState.feelingName}
                                  initialSelectedDate={
                                    checkInState.selectedDate
                                  }
                                  initialExistingRecord={
                                    checkInState.existingRecord
                                  }
                                >
                                  {checkInState.existingRecord ? (
                                    <CheckInViewWithState
                                      stateOverride={checkInState}
                                      onClose={() => {
                                        closeDrawer();
                                        navigate("/app/home");
                                      }}
                                    />
                                  ) : (
                                    <CheckInAddWithState
                                      stateOverride={checkInState}
                                      onClose={() => {
                                        closeDrawer();
                                        navigate("/app/home");
                                      }}
                                    />
                                  )}
                                </CheckInProvider>
                              ) : null}
                            </div>
                          </Drawer.Content>
                        </Drawer.Portal>
                      </Drawer.Root>
                    )}
                      { !isFriday && isCheckInDay && (
                      <>
                        <p>Check in is already done</p>
                        <p
                        onClick={
                        () => {localStorage.setItem("checkIn", "no")}
                        }
                        >Discard</p>
                      </>
                    )}
                    {
                      isFriday && false && (
                       <>
                         It's Friday
                       </>
                      )
                    }
                    {teamId != null && activeTeam && !isCheckInDay && false && (
                      <>Today is not the check-in day for {activeTeam.name}</>
                    )}
                    <br />
                    {isCheckInDay && !hideHomeChromeFromIntro4 && (
                      <>
                        <ChooseAnotherDate
                          selectedDate={selectedDate}
                          setSelectedDate={setSelectedDate}
                        />
                      </>
                    )}
                  </div>
                </>
              )}

              {teamId == null && organizationId == null && (
                <div
                  className="home-inner-wrapper auth-options-header"
                  style={{
                    ...INNER_WRAPPER_STYLE,
                    minHeight: "calc(100vh - 80px)",
                    width: "100%",
                  }}
                >
                  2. Upgrade to add your teams &nbsp;
                  <p>Seems that you are a new user</p>
                  <p>I do own this domain X</p>
                  <p>Upgrade to invite new people</p>
                  {/*
                  <br />
                  OR Join existing team with code
                  <JoinTeamHome />
                  */}
                </div>
              )}

              {!isStillChecking &&
                hasActiveSubscription === false &&
                !isFreeAccount && (
                  <>
                    no subscription
                    <br />
                    check to proceed experienceapp.io/premium
                  </>
                )}

              {teams.length == 0 && organizationId != null && (
                <>
                  {/*<SetupTeam />*/}
                  <button onClick={() => setShowAddTeamModal(true)}>
                    Time to create first team, admin
                  </button>
                  {showAddTeamModal && <SheetSide />}
                </>
              )}

              {teams.length > 0 &&
                organizationId != null &&
                teamId === null && (
                  <>
                    {/*<SetupTeam />*/}
                    <button onClick={() => setShowAddTeamModal(true)}>
                      Join team or create one
                    </button>
                    {showAddTeamModal && <SheetSide />}
                  </>
                )}
            </div>
          </div>
        </div>

        <div className="buttons-wrapper">
          <ButtonsSection />
        </div>
      </div>
    </>
  );
}
